import { Injectable } from '@angular/core';
import { HttpContextToken, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CatchitErrorHandlerService } from '../services/catchit-error-handler.service';

/**
 * Set on a request's HttpContext to suppress this interceptor's own error toast for an
 * expected, already-handled error (e.g. a 404 the caller treats as a legitimate "not
 * found" result, not a failure). Doesn't affect @uoa/error-pages' separate interceptor -
 * see BypassErrorService for that one.
 */
export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private errorHandler: CatchitErrorHandlerService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (req.context.get(SUPPRESS_ERROR_TOAST)) {
          return throwError(() => error);
        }

        // Let the error handler service deal with the error
        this.errorHandler.handleHttpError(error, {
          url: req.url,
          method: req.method,
          action: this.getActionFromUrl(req.url)
        });

        // For certain error types, we might want to continue the error chain
        // For others, we'll return a handled response
        if (error.status === 401 || error.status === 403) {
          // Let auth errors propagate for route guards to handle
          return throwError(() => error);
        }

        // For other errors, we've shown the user message, so return empty
        return throwError(() => error);
      })
    );
  }

  private getActionFromUrl(url: string): string {
    // Extract meaningful action name from URL for better error context
    if (url.includes('/line-checks')) return 'line-check';
    if (url.includes('/users')) return 'user-management';
    if (url.includes('/auth')) return 'authentication';
    if (url.includes('/profile')) return 'profile';
    return 'api-request';
  }
}